import { useScienceTutorSection, tutorSelection } from './scienceTutorContext';
import { FlaskConical, ListChecks } from 'lucide-react';
import { useState } from 'react';

import {
  checkConcordantSelection,
  markVolumetricAnswer,
  solveTitration,
  type VolumetricCheck,
} from './volumetricAnalysis';
import {
  ACID_BASE_TITRATIONS,
  BACK_TITRATIONS,
  REDOX_TITRATIONS,
  type TitrationProblem,
} from './volumetricAnalysisData';
import { AcidBaseFeedback } from './AcidBaseCalculationsVisuals';
import { BuretteSchematic, MoleMap, TitreChart } from './VolumetricAnalysisVisuals';

const PROBLEMS_BY_MODE: Record<TitrationProblem['mode'], TitrationProblem[]> = {
  'acid-base': ACID_BASE_TITRATIONS,
  back: BACK_TITRATIONS,
  redox: REDOX_TITRATIONS,
};

const runLabel = (index: number) => (index === 0 ? 'Rough' : `Run ${index + 1}`);

function SetupDetails({ problem }: { problem: TitrationProblem }) {
  const spec = problem.spec;
  const rows: { term: string; detail: string }[] =
    spec.kind === 'back'
      ? [
          { term: 'Reaction 1', detail: spec.equations[0] },
          { term: 'Reaction 2', detail: spec.equations[1] },
          { term: 'Sample', detail: `${spec.sample.massG} g ${spec.sample.name}` },
          {
            term: 'Excess reagent',
            detail: `${spec.excess.volumeMl} mL of ${spec.excess.concentration} mol L⁻¹ ${spec.excess.formula}`,
          },
          {
            term: 'Burette',
            detail: `${spec.titrant.concentration} mol L⁻¹ ${spec.titrant.formula}`,
          },
          ...(spec.aliquot
            ? [
                {
                  term: 'Aliquot',
                  detail: `${spec.aliquot.aliquotMl} mL from a ${spec.aliquot.flaskMl} mL flask`,
                },
              ]
            : [{ term: 'Aliquot', detail: 'Whole mixture titrated' }]),
          { term: 'Indicator', detail: spec.indicator },
        ]
      : [
          { term: 'Equation', detail: spec.equation },
          {
            term: 'Burette',
            detail: `${spec.titrant.concentration ? `${spec.titrant.concentration} mol L⁻¹ ` : 'unknown '}${spec.titrant.formula} (${spec.titrant.name})`,
          },
          {
            term: 'Conical flask',
            detail: `${spec.aliquotMl} mL aliquot of ${spec.analyte.concentration ? `${spec.analyte.concentration} mol L⁻¹ ` : ''}${spec.analyte.formula} (${spec.analyte.name})`,
          },
          { term: 'Indicator', detail: spec.indicator },
        ];
  return (
    <dl className="grid gap-2 sm:grid-cols-2">
      {rows.map((row) => (
        <div key={row.term} className="min-w-0 rounded-2xl bg-surface px-3 py-2">
          <dt className="text-xs font-extrabold text-slate2">{row.term}</dt>
          <dd className="break-words text-sm font-bold text-ink">{row.detail}</dd>
        </div>
      ))}
    </dl>
  );
}

export function VolumetricAnalysisTitration({ mode }: { mode: TitrationProblem['mode'] }) {
  const problems = PROBLEMS_BY_MODE[mode];
  const [problemId, setProblemId] = useState(problems[0].id);
  const [selected, setSelected] = useState<number[]>([]);
  const [selectionCheck, setSelectionCheck] = useState<VolumetricCheck | null>(null);
  const [answer, setAnswer] = useState('');
  const [answerCheck, setAnswerCheck] = useState<VolumetricCheck | null>(null);
  const [buretteRun, setBuretteRun] = useState(0);
  useScienceTutorSection('VolumetricAnalysisTitration', { problemId, selected: tutorSelection(selected), answer, buretteRun });
  const problem = problems.find((candidate) => candidate.id === problemId) ?? problems[0];
  const solved = solveTitration(problem);
  const model = solved.value;
  const titres = problem.spec.titres;
  const revealed =
    selectionCheck?.status === 'correct' ||
    (answerCheck !== null && answerCheck.status !== 'invalid');
  const concordant = model?.concordant?.indices ?? [];

  const choose = (id: string) => {
    setProblemId(id);
    setSelected([]);
    setSelectionCheck(null);
    setAnswer('');
    setAnswerCheck(null);
    setBuretteRun(0);
  };

  const toggle = (index: number) => {
    setSelected((current) =>
      current.includes(index) ? current.filter((value) => value !== index) : [...current, index],
    );
    setSelectionCheck(null);
  };

  return (
    <section
      aria-label="Titration problems"
      className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8"
    >
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-wash-sunshine text-ink">
          <ListChecks className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
            Choose titres, then calculate
          </p>
          <h2 className="text-xl font-extrabold text-ink">{problems.length} reviewed titrations</h2>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap gap-2" role="group" aria-label="Choose a titration">
        {problems.map((candidate, index) => (
          <button
            key={candidate.id}
            type="button"
            aria-pressed={candidate.id === problem.id}
            onClick={() => choose(candidate.id)}
            title={candidate.title}
            className={`rounded-full border px-3 py-1.5 text-sm font-extrabold transition-colors motion-reduce:transition-none ${
              candidate.id === problem.id
                ? 'border-brand-sky bg-wash-sky text-ink'
                : 'border-hairline bg-white text-slate2 hover:border-brand-sky/40'
            }`}
          >
            {index + 1}
            <span className="sr-only">. {candidate.title}</span>
          </button>
        ))}
      </div>

      <h3 className="mt-6 text-lg font-extrabold text-ink">{problem.title}</h3>
      <p className="mt-1 font-medium leading-7 text-ink-soft">{problem.context}</p>
      <div className="mt-4">
        <SetupDetails problem={problem} />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-4">
          <fieldset className="rounded-[1.5rem] border border-hairline p-4">
            <legend className="px-1 text-sm font-extrabold text-ink">
              Step 1 · Tick the titres you would average
            </legend>
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {titres.map((titre, index) => (
                <label
                  key={`${problem.id}-${index}`}
                  className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm font-bold ${
                    selected.includes(index)
                      ? 'border-brand-sky bg-wash-sky text-ink'
                      : 'border-hairline bg-white text-slate2'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(index)}
                    onChange={() => toggle(index)}
                    onFocus={() => setBuretteRun(index)}
                    className="h-4 w-4 accent-ink"
                  />
                  <span>
                    {runLabel(index)}:{' '}
                    <span className="tabular-nums text-ink">{titre.toFixed(2)} mL</span>
                  </span>
                </label>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setSelectionCheck(checkConcordantSelection(titres, selected))}
              className="mt-3 rounded-full bg-ink px-5 py-2.5 text-sm font-extrabold text-white"
            >
              Check titres
            </button>
            {selectionCheck ? (
              <div className="mt-3" aria-label="Titre feedback" role="region">
                <AcidBaseFeedback check={selectionCheck} working={[]} />
              </div>
            ) : null}
          </fieldset>

          {model ? (
            <div className="space-y-3 rounded-[1.5rem] border border-hairline p-4">
              <label htmlFor="volumetric-answer" className="block text-sm font-extrabold text-ink">
                Step 2 · Your answer for {model.quantityLabel} ({model.unit})
              </label>
              <div className="flex gap-2">
                <input
                  id="volumetric-answer"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={answer}
                  onChange={(event) => {
                    setAnswer(event.target.value);
                    setAnswerCheck(null);
                  }}
                  className="min-w-0 flex-1 rounded-xl border border-hairline bg-surface px-4 py-3 font-bold text-ink focus:border-brand-sky focus:outline-none focus:ring-4 focus:ring-brand-sky/15"
                />
                <span className="grid place-items-center rounded-xl bg-surface px-3 text-sm font-extrabold text-slate2">
                  {model.unit}
                </span>
              </div>
              <button
                type="button"
                disabled={!answer.trim()}
                onClick={() => setAnswerCheck(markVolumetricAnswer(solved, answer))}
                className="rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                Check answer
              </button>
              {answerCheck ? (
                <div aria-label="Answer feedback" role="region">
                  <AcidBaseFeedback check={answerCheck} working={model.working} />
                </div>
              ) : null}
            </div>
          ) : (
            <p role="alert" className="rounded-2xl bg-wash-sunshine p-4 text-sm font-bold text-ink">
              {solved.error}
            </p>
          )}
        </div>

        <div className="min-w-0 space-y-5 rounded-[1.5rem] border border-hairline bg-surface p-4 sm:p-5">
          <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
            <FlaskConical className="h-4 w-4" aria-hidden="true" /> Model view
          </p>
          <TitreChart
            revealed={revealed}
            rows={titres.map((value, index) => ({
              label: runLabel(index),
              value,
              rough: index === 0,
              concordant: concordant.includes(index),
            }))}
          />
          <div className="flex flex-wrap items-start gap-4">
            <BuretteSchematic
              readingMl={titres[buretteRun] ?? titres[0]}
              runLabel={runLabel(buretteRun)}
            />
            <div
              className="flex flex-wrap gap-1"
              role="group"
              aria-label="Show a run in the burette"
            >
              {titres.map((_, index) => (
                <button
                  key={`burette-${index}`}
                  type="button"
                  aria-pressed={buretteRun === index}
                  onClick={() => setBuretteRun(index)}
                  className={`rounded-full border px-2.5 py-1 text-xs font-extrabold ${
                    buretteRun === index
                      ? 'border-ink bg-ink text-white'
                      : 'border-hairline bg-white text-slate2'
                  }`}
                >
                  {runLabel(index)}
                </button>
              ))}
            </div>
          </div>
          {revealed && model ? (
            <div>
              <p className="mb-2 text-xs font-extrabold uppercase tracking-[0.14em] text-slate2">
                Mole map
              </p>
              <MoleMap steps={model.flow} />
            </div>
          ) : (
            <p className="rounded-2xl border-2 border-dashed border-hairline px-4 py-4 text-center text-sm font-bold text-slate2">
              Check your titres or your answer to reveal the concordant set and the mole map.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
